import { useCallback, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, CircularProgress, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { WithStyles, withStyles } from '@mui/styles';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { debounce } from 'lodash';
import CircleIcon from '@mui/icons-material/Circle';

// Contexts
import { withTenant, withTranslate } from '../../core/TenantProvider';
// Components, atoms, molecules, organisms
import APAlertMessage from '../../components/atoms/APAlertMessage';
import APBackdropLoader from '../../components/atoms/APBackdropLoader';
import APButton from '../../components/atoms/APButton';
import APFormMapper from '../../components/organisms/APFormMapper';
import APLabel from '../../components/atoms/APLabel';
import AEDocumentUpload from '../../components/organisms/AEDocumentUpload';
import APHeading from '../../components/atoms/APHeading';
// Files in the same folder
import { ValidationSchema } from './MappingDefinition/validationSchema';
import AddBankDetailsContainerStyle from './styles/AddBankDetailsContainer.style';
import { getBankDetailsFieldMappingDefinition, getSortCodeForPayload } from './MappingDefinition/fieldsMappingDefinition';
//Redux
import { getServicesErrorObj } from '../../redux/modules/servicesDowntime/actions';
import { action as BankDetailsActions } from '../../redux/modules/bankDetails';
import { ReducerState } from '../../redux/modules';
import { RetrieveBankDetailObjectType } from '../../redux/modules/bankDetails/reducer';
// Types
import { TranslateFunction } from '../../core/types/TypographyTypes';
import { AffiliateDetailsAttributes, RoleAccessFunction } from '../../core/types/AffiliateDetails';
import { CountriesApiObject } from '../../core/types/CountriesList';
import { CurrenciesAPIObject } from '../../core/types/Currencies';
// Utils
import GetBasicDetails from '../../core/utils/GetBasicDetails';
import { RemoveEmptyKeys } from '../../core/utils/Formatter/ObjectFormat';
import { getCountryISO2 } from '../../components/organisms/APBankAccountLookup/utils/isoMapper';
import { GetCountryObjectAsPerCountryCode, GetCurrenciesObjectAsPerCurrencyCode } from '../../core/utils/GetCountriesData';
import { FileToBase64 } from '../../core/utils/CreateFile/CreateFile';
import { ReactComponent as InformationIcon } from "./assets/icons/informationFilled.svg";
// Constants
import { COUNTRY_WISE_BANK_DETAILS_FIELDS } from '../../core/utils/Constants/Constants';

interface AddBankDetailsContainerProps extends WithStyles<typeof AddBankDetailsContainerStyle> {
    translate: TranslateFunction;
    retrieveLoadingState: string;
    settings: any;
    getServicesErrorObj: Function;
    updateTranslations: Function;
    retrieveBankDetails: Function;
    resetRetrieveBankDetails: Function;
    createLoadingState: string;
    getCountryDetailsFetch: Function;
    getCurrencyDetailsFetch: Function;
    createBankDetails: Function;
    addBankDetailsSectionClose: Function;
    retrieveBankDetailsObject: null | RetrieveBankDetailObjectType;
    affiliateDetails: AffiliateDetailsAttributes | null;
    getCountryDetails: any | null;
    getCurrencyDetails: any | null,
    bankDetailsDocUploadResponse: any | null;
    uploadBankDetailsDocument: Function;
    resetUploadBankDetails: Function;
    roleAccessFunction: RoleAccessFunction;
    bankDetailsDocUploadLoading: string;
}

export const StyledPaper = styled(Box)({
    position: 'relative',
    marginBottom: 30,
    padding: 30,
    background: '#FFFFFF',
    boxShadow: '0 1px 4px 0 rgba(0,0,0,0.08)',
    '& .MuiFormLabel-root': {
        '&.Mui-error': {
            color: '#f44336'
        }
    },
    '& .errorMessage': {
        width: '100%',
        margin: '0 0 32px 0',
        '& .MuiCollapse-root': {
            margin: '0 0 25px 0',
            '&.MuiCollapse-hidden': {
                margin: '0',
            }
        },
        '& .MuiAlert-action': {
            position: 'absolute',
            right: -13,
            margin: '0 !important',
            top: -8,
            padding: 0,
        },
        '& .MuiPaper-root': {
            position: 'relative',
            padding: '6px 20px',
            width: '100%',
            boxSizing: 'border-box'
        },
        '& .MuiCollapse-wrapperInner': {
            margin: 0,
            '& .MuiAlert-message': {
                padding: '13px 25px 13px 10px',
                width: '100%',
                boxSizing: 'border-box',
                display: 'block',
                margin: 0,
                '& strong': {
                    textTransform: 'uppercase'
                }
            }
        },
    }
});

const {
    organisationCode: ORGANISATION
} = GetBasicDetails();

const AddBankDetailsContainer = (props: AddBankDetailsContainerProps) => {
    const { classes, translate, retrieveBankDetails, retrieveLoadingState, retrieveBankDetailsObject,
        resetRetrieveBankDetails, createLoadingState, getCountryDetailsFetch, getCountryDetails, getCurrencyDetailsFetch,
        getCurrencyDetails, createBankDetails, affiliateDetails, addBankDetailsSectionClose, bankDetailsDocUploadLoading,
        bankDetailsDocUploadResponse, uploadBankDetailsDocument, resetUploadBankDetails, roleAccessFunction
    } = props;
    const { bank_name, address, recommended_bic } = retrieveBankDetailsObject || {};
    const { BANK_DOCUMENT_UPLOAD } = roleAccessFunction || {};

    const countryDependantFields = {
        accountNumber: '',
        sortCode: '',
        bsbNumber: '',
        transitNumber: '',
        abaNumber: '',
        branchCode: '',
        ifscCode: '',
        iban: '',
        cnapsCode: '',
        swiftCode: '',
    }

    const formDefaultValues = {
        accountName: '',
        currency: '',
        country: '',
        isCountryBlackListed: false,
        affiliateBank: '',
        branchAddress: '',
        file: null,
        ...countryDependantFields
    }

    const removeTrailingXXX = (swiftCode: string | undefined) => {
        if (typeof swiftCode !== 'string') return '';

        // Remove trailing "XXX" only if it's at the end
        return swiftCode.replace(/XXX$/, '');
    }

    const [isManualEdit, setIsManualEdit] = useState<boolean>(false);
    const [formValue, setFormValue] = useState<any>(formDefaultValues);
    const [selectedCountryDetail, setSelectedCountryDetail] = useState<any>(null);
    const [selectedCurrencyDetail, setSelectedCurrencyDetail] = useState<any>(null);
    const [isCountryBlacklisted, setIsCountryBlacklisted] = useState<any>(false);
    const formated_recommended_bic = removeTrailingXXX(recommended_bic);
    const [removeFileUpload, setRemoveFileUpload] = useState<boolean>(false);

    const bankDetailsValidation: boolean = isManualEdit || (retrieveLoadingState === 'LOADED' && !retrieveBankDetailsObject?.bank_name);

    const {
        control,
        handleSubmit,
        formState,
        watch,
        setValue,
        getValues,
        trigger,
        clearErrors,
        reset
    } = useForm({
        mode: 'all',
        reValidateMode: 'onChange',
        resolver: yupResolver(ValidationSchema(bankDetailsValidation, BANK_DOCUMENT_UPLOAD)),
        defaultValues: { ...formValue }
    });

    const { isValid, errors, } = formState;

    const countryInput: string = watch('country');
    const currencyInput: string = watch('currency');
    const accountNameInput: string = watch('accountName');
    const affiliateBankInput: string | undefined = watch('affiliateBank');
    const swiftCodeInput: string | undefined = watch('swiftCode');
    const branchAddressInput: string | undefined = watch('branchAddress');

    useEffect(() => {
        setIsManualEdit(false);
        resetRetrieveBankDetails();

        if (countryInput) {
            const selectedCountryDetail: CountriesApiObject | null = GetCountryObjectAsPerCountryCode(countryInput, getCountryDetails);

            setSelectedCountryDetail(selectedCountryDetail);
        }

        if (currencyInput) {

            const selectedCurrencyDetail: CurrenciesAPIObject | null = GetCurrenciesObjectAsPerCurrencyCode(currencyInput, getCurrencyDetails);
            setSelectedCurrencyDetail(selectedCurrencyDetail);
        }

        const resetFormFiled = {
            accountName: accountNameInput,
            currency: currencyInput,
            country: countryInput,
            isCountryBlackListed: isCountryBlacklisted,
            affiliateBank: '',
            branchAddress: '',
            ...countryDependantFields
        }

        reset(resetFormFiled, { keepErrors: true, keepTouched: true, keepDirty: true });

        trigger();
    }, [countryInput, currencyInput]);

    useEffect(() => {
        if (retrieveLoadingState === 'LOADED' && retrieveBankDetailsObject && retrieveBankDetailsObject.bank_name) {
            setValue("affiliateBank", bank_name);
            setValue("branchAddress", address);
            setValue("swiftCode", formated_recommended_bic);
        } else if (retrieveLoadingState === 'ERROR' || (retrieveLoadingState === 'LOADED' && !retrieveBankDetailsObject?.bank_name)) {
            setIsManualEdit(true);
            window.scrollTo(0, 0);
        }
    }, [retrieveBankDetailsObject, retrieveLoadingState]);


    useEffect(() => {
        resetRetrieveBankDetails();
        (getCountryDetails === null) && getCountryDetailsFetch();
        (getCurrencyDetails === null) && getCurrencyDetailsFetch();
    }, []);

    useEffect(() => {
        if (selectedCountryDetail && selectedCountryDetail.blackListed === 'ACTIVE') {
            setIsCountryBlacklisted(true);
            setValue("isCountryBlackListed", true);
        } else {
            setIsCountryBlacklisted(false);
            setValue("isCountryBlackListed", false);
        }

        setIsManualEdit(false)
    }, [selectedCurrencyDetail])

    useEffect(() => {
        if (selectedCountryDetail) {
            if (selectedCountryDetail.blackListed === 'ACTIVE') {
                setIsCountryBlacklisted(true);
                setValue("isCountryBlackListed", true);
            } else {
                setIsCountryBlacklisted(false);
                setValue("isCountryBlackListed", false);
            }
        }

        setIsManualEdit(false)
    }, [selectedCountryDetail]);

    useEffect(() => {
        if (createLoadingState === 'SUCCESS' || createLoadingState === 'ERROR') {
            addBankDetailsSectionClose();
        }
    }, [createLoadingState]);

    const debouncedTrigger = useCallback(
        debounce((fields) => {
            trigger(fields);
        }, 500),
        [trigger]
    );

    useEffect(() => {
        if (!!swiftCodeInput) {
            debouncedTrigger(['bsbNumber', 'transitNumber', 'abaNumber', 'branchCode', 'ifscCodeField', 'iban', 'cnapsCodeNumberField']);
        }
    }, [swiftCodeInput]);

    const onRetrieveClick = () => {
        const paymentCountryValue = getValues('country');
        const countryCode = getCountryISO2(paymentCountryValue);

        let selectedFields: any = {
            "organisation": ORGANISATION,
            "country": countryCode,
            'account_number': getValues('accountNumber'),
            'sort_code': getSortCodeForPayload(getValues('sortCode'))
        };

        COUNTRY_WISE_BANK_DETAILS_FIELDS.forEach((obj) => {
            if (obj.values.includes(paymentCountryValue)) {
                selectedFields[obj.payloadKeyName] = getValues(obj.type);
            }
        });

        if (countryCode === 'GB') {
            selectedFields['account_number'] = ''
        }

        const finalPayload = RemoveEmptyKeys(selectedFields);

        retrieveBankDetails(finalPayload);
    }

    useEffect(() => {
        if (bankDetailsDocUploadLoading === 'SUCCESS' && bankDetailsDocUploadResponse && createLoadingState === 'UNINITIALIZED') {
            createBankDetails(getFormDataForApi(formValue), affiliateDetails, selectedCountryDetail, selectedCurrencyDetail, bankDetailsDocUploadResponse);
        }
        if (createLoadingState === 'ERROR' && bankDetailsDocUploadResponse) {
            resetUploadBankDetails();
        }
    }, [bankDetailsDocUploadLoading, bankDetailsDocUploadResponse, createLoadingState])


    const maskNumberExceptLast3 = (value: string): string => {
        if (!value) return '';
        const visiblePart = value.slice(-3);
        const maskedPart = value.slice(0, -3).replace(/\d/g, 'X');
        return maskedPart + visiblePart;
    }

    const onBankDetailsDocumentUpload = async (formData: any) => {
        const { accountNumber, file, iban } = formData || {};
        const { affiliate } = affiliateDetails || {};
        const base64EncodedFileString = await FileToBase64(file);
        const { organisationCode } = GetBasicDetails();
        const accountOrIbanNumber = accountNumber || iban;

        const payload = {
            "base64EncodedString": [
                base64EncodedFileString
            ],
            "crmAccountID": affiliate?.SF_ID ?? '',
            "crmContactID": affiliate?.SF_ID ?? '',
            "orgCode": organisationCode,
            "source": "AffiliatePortal",
            "withDetails": false,
            "fileNames": [
                file.name,
            ],
            "isChunked": false,
            "note": "qq",
            "documentType": `Bank Verification-${maskNumberExceptLast3(accountOrIbanNumber)}`,
            "createdBy": `${affiliate?.user_first_name || ''} ${affiliate?.user_last_name || ''}`,
            "isAuthorized": [
                true
            ],
        }

        uploadBankDetailsDocument(payload);
    }

    const getFormDataForApi = (data: any) => ({
        ...data,
        sortCode: getSortCodeForPayload(data?.sortCode ?? '')
    });

    const onSubmit = (formData: any) => {
        const payload = getFormDataForApi(formData);
        if (BANK_DOCUMENT_UPLOAD) {
            setFormValue(formData);
            onBankDetailsDocumentUpload(payload);
        } else {
            createBankDetails(payload, affiliateDetails, selectedCountryDetail, selectedCurrencyDetail);
        }
    };

    const onClose = () => {
        resetRetrieveBankDetails();
    };

    const onFileUpload = async (fileData: { fileName: string; roundedFileSize: string; fileUploaded: File; files: any }) => {
        const { fileUploaded } = fileData;

        errors['file'] && clearErrors('file');

        setRemoveFileUpload(false);

        setValue('file', fileUploaded);

        trigger('file');
    };

    const removedFileSuccess = () => {
        setValue('file', null);
        trigger('file');
    };

    const showBankDetailsSummary: boolean = !!(retrieveBankDetailsObject && !isManualEdit && retrieveLoadingState === 'LOADED');
    const showBankDocumentUploadSection: boolean = !!((retrieveBankDetailsObject && retrieveLoadingState === 'LOADED') || isManualEdit);
    const showRetrieveBankButton: boolean = watch(['accountName', 'currency', 'country']).every(
        (field: string | null | undefined) => field !== "" && field !== null && field !== undefined
    );

    const isError: boolean = retrieveLoadingState === 'ERROR' || (retrieveLoadingState === 'LOADED' && !retrieveBankDetailsObject?.bank_name);
    const isRetrieveBankDetailsSuccess: boolean = retrieveLoadingState === 'LOADED' && affiliateBankInput !== '';
    const isBankDetailsValid: boolean = !!affiliateBankInput && !!branchAddressInput;

    return (
        <div className={classes.addBankDetailsContainer}>
            {createLoadingState === 'LOADING' &&
                <APBackdropLoader open={true} />
            }

            <div className={classes.addBankDetailsContainerTitle}>
                <h1>{translate('add.bank.details.heading')}</h1>
                <p>{translate('add.bank.details.paragraph')}</p>
            </div>
            <div className={classes.addBankDetailsFormSteps}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <StyledPaper>
                        {isError &&
                            <APAlertMessage
                                className={`errorMessage`}
                                message={translate("bank.account.lookup.bankdetails.alert.message")}
                                title={translate("bank.account.lookup.bankdetails.alert.title")}
                                severity={"error"}
                                icon={'error'}
                                hideCloseIcon={false}
                                onClose={onClose} />
                        }
                        <APFormMapper
                            control={control}
                            errors={errors}
                            fields={getBankDetailsFieldMappingDefinition({ translate, classes, isManualEdit, countryInput })}
                            watch={watch}
                            translate={translate}
                        />
                        {showRetrieveBankButton && !isManualEdit &&
                            <>
                                {!isRetrieveBankDetailsSuccess &&
                                    <>
                                        <div className={classes.stepButtonsLeft}>
                                            <APButton
                                                children={translate('add.bank.details.form.retrive.button.label')}
                                                className={classes.button}
                                                color='primary'
                                                variant='contained'
                                                onClick={() => onRetrieveClick()}
                                                disabled={!isValid || retrieveLoadingState === 'LOADING'}
                                            />
                                            {retrieveLoadingState === 'LOADING' && <CircularProgress className={classes.loadingIcon} size={25} />}
                                        </div>

                                        <div className={classes.stepButtonsLeft}>
                                            <APButton
                                                children={translate('add.bank.details.form.edit.manually.button.label')}
                                                className={classes.manualTextButton}
                                                color='primary'
                                                variant='text'
                                                onClick={() => setIsManualEdit(true)}
                                            />
                                        </div>
                                    </>
                                }
                            </>
                        }
                        {showBankDetailsSummary &&
                            <div className={classes.selectionSummary}>
                                <div>
                                    <APLabel className={classes.summaryLabel} children={translate('add.bank.details.form.summary.bank.name.label')} />
                                    <APLabel className={classes.summaryText}>{bank_name}</APLabel>
                                </div>

                                <div>
                                    <APLabel className={classes.summaryLabel} children={translate('add.bank.details.form.summary.bank.address.label')} />
                                    <APLabel className={classes.summaryText}>{address}</APLabel>
                                </div>

                                <div>
                                    <APLabel className={classes.summaryLabel} children={translate('add.bank.details.form.summary.bank.swift.label')} />
                                    <APLabel className={classes.summaryText}>{formated_recommended_bic}</APLabel>
                                </div>

                                <APButton className={classes.textButton} onClick={() => setIsManualEdit(true)} color="primary">
                                    {translate('add.bank.details.form.summary.edit.details')}
                                </APButton>
                            </div>
                        }
                        {showBankDocumentUploadSection && BANK_DOCUMENT_UPLOAD &&
                            <Box>
                                <APHeading
                                    className={classes.formSectionHeading}
                                    variant='body1'
                                >
                                    {translate('add.bank.details.form.bank.certificate.heading')}
                                </APHeading>
                                <div className={classes.certificateConditionSummary}>
                                    <Box className={classes.certificateSummaryHeader}>
                                        <InformationIcon />
                                        <Typography variant="body1" fontWeight={500}>
                                            {translate('add.bank.details.bank.certificate.requirement.header')}
                                        </Typography>
                                    </Box>
                                    <List className={classes.certificateSummaryList}>
                                        {[
                                            'add.bank.details.bank.certificate.requirement.one',
                                            'add.bank.details.bank.certificate.requirement.two',
                                            'add.bank.details.bank.certificate.requirement.three',
                                            'add.bank.details.bank.certificate.requirement.four',
                                        ].map((text, index) => (
                                            <ListItem key={index} >
                                                <ListItemIcon sx={{ minWidth: 20, color: '#4A4A4A' }} >
                                                    <CircleIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="body1">
                                                            {translate(text)}
                                                        </Typography>
                                                    }
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </div>
                                <AEDocumentUpload
                                    onFileUpload={onFileUpload}
                                    inputLabel={''}
                                    required={false}
                                    removeFileUpload={removeFileUpload}
                                    removedFile={removedFileSuccess}
                                    errorMessage={null}
                                />
                            </Box>
                        }
                    </StyledPaper>
                    <div className={classes.stepButtons}>
                        <div className={classes.stepButtonsLeft}>
                            <APButton
                                children={translate('add.bank.details.back.button.label')}
                                className={classes.stepButton}
                                color='primary'
                                variant='outlined'
                                onClick={() => addBankDetailsSectionClose()}
                            />
                        </div>
                        <div className={classes.stepButtonsRight}>
                            <APButton
                                children={translate('add.bank.details.confirm.button.label')}
                                className={classes.stepButton}
                                color='primary'
                                disabled={!isValid || isCountryBlacklisted || !isBankDetailsValid}
                                variant='contained'
                                type='submit'
                            />
                            {(bankDetailsDocUploadLoading === 'LOADING' || createLoadingState === 'LOADING') && <CircularProgress className={classes.loadingIcon} size={25} />}
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
};

const mapStateToProps = (store: ReducerState) => ({
    retrieveBankDetailsObject: store.bankDetails.retrieveBankDetails,
    retrieveLoadingState: store.bankDetails.retrieveLoadingState,
    createLoadingState: store.bankDetails.createLoadingState,
    getCountryDetails: store.bankDetails.getCountryDetails,
    getCurrencyDetails: store.bankDetails.getCurrencyDetails,
    affiliateDetails: store.user.affiliateDetails,
    bankDetailsDocUploadLoading: store.bankDetails.bankDetailsDocUploadLoadingState,
    bankDetailsDocUploadResponse: store.bankDetails.bankDetailsDocUploadResponse,
    roleAccessFunction: store.user.roleAccessFunction
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
    getServicesErrorObj: getServicesErrorObj,
    retrieveBankDetails: BankDetailsActions.retrieveBankDetails,
    resetRetrieveBankDetails: BankDetailsActions.resetRetrieveBankDetails,
    getCountryDetailsFetch: BankDetailsActions.getCountryDetails,
    getCurrencyDetailsFetch: BankDetailsActions.getCurrencyDetails,
    createBankDetails: BankDetailsActions.createBankDetails,
    uploadBankDetailsDocument: BankDetailsActions.uploadBankDetailsDocument,
    resetUploadBankDetails: BankDetailsActions.resetUploadBankDetails,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(
    withTenant(
        withTranslate(
            withStyles(AddBankDetailsContainerStyle)(
                AddBankDetailsContainer
            )
        )
    )
);